import { fetchJson } from "./http";
import type {
  MeResponse,
  TenantInvitation,
  TenantMember,
  TenantRole,
  TenantSlug,
  User,
  UserSession,
} from "./types";

function authHeaders(accessToken: string): Record<string, string> {
  return { Authorization: `Bearer ${accessToken}` };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function normalizeTenantMember(raw: unknown): TenantMember | null {
  // Expected frontend shape:
  // { user: { id, email, username? }, role, joined_at? }
  if (!isRecord(raw)) return null;

  const maybeUser = raw.user;
  if (isRecord(maybeUser)) {
    const id = typeof maybeUser.id === "string" ? maybeUser.id : null;
    const email = typeof maybeUser.email === "string" ? maybeUser.email : null;
    const username =
      typeof maybeUser.username === "string" || maybeUser.username === null
        ? (maybeUser.username as string | null)
        : undefined;
    const role = typeof raw.role === "string" ? raw.role : "";
    const joinedAt = typeof raw.joined_at === "string" ? raw.joined_at : undefined;
    if (!id || !email) return null;
    return { user: { id, email, username }, role, joined_at: joinedAt };
  }

  // Backend currently returns:
  // { user_id, email, username?, role, created_at }
  const userId = typeof raw.user_id === "string" ? raw.user_id : null;
  const email = typeof raw.email === "string" ? raw.email : null;
  const username =
    typeof raw.username === "string" || raw.username === null
      ? (raw.username as string | null)
      : undefined;
  const role = typeof raw.role === "string" ? raw.role : "";
  const createdAt = typeof raw.created_at === "string" ? raw.created_at : undefined;

  if (!userId || !email) return null;
  return { user: { id: userId, email, username }, role, joined_at: createdAt };
}

// ---- Current user ----

function normalizeUser(raw: unknown): User | null {
  if (!isRecord(raw)) return null;
  const id = typeof raw.id === "string" ? raw.id : null;
  const email = typeof raw.email === "string" ? raw.email : null;
  const username =
    typeof raw.username === "string" || raw.username === null
      ? (raw.username as string | null)
      : undefined;
  if (!id || !email) return null;
  return {
    id,
    email,
    username,
    created_at: typeof raw.created_at === "string" ? raw.created_at : undefined,
    updated_at: typeof raw.updated_at === "string" ? raw.updated_at : undefined,
    disabled_at:
      typeof raw.disabled_at === "string" || raw.disabled_at === null
        ? (raw.disabled_at as string | null)
        : undefined,
  };
}

export async function getCurrentUser(accessToken: string, signal?: AbortSignal): Promise<User> {
  const res = await fetchJson<MeResponse>(
    "/users/me",
    {
      headers: authHeaders(accessToken),
      signal,
    },
    undefined,
  );
  const user = isRecord(res) && isRecord(res.user) ? normalizeUser(res.user) : null;
  if (!user) throw new Error("Invalid response from server");
  return user;
}

export async function updateCurrentUser(
  accessToken: string,
  input: { email?: string; password?: string },
  signal?: AbortSignal,
): Promise<User> {
  const body: Record<string, string> = {};
  if (input.email != null) body.email = input.email;
  if (input.password != null) body.new_password = input.password;
  const res = await fetchJson<MeResponse>(
    "/users/me",
    {
      method: "PATCH",
      headers: authHeaders(accessToken),
      body,
      signal,
    },
    undefined,
  );
  const user = isRecord(res) && isRecord(res.user) ? normalizeUser(res.user) : null;
  if (!user) throw new Error("Invalid response from server");
  return user;
}

/** Request email change (sends confirmation links to old and new email; both must be clicked). */
export async function requestEmailChange(
  accessToken: string,
  newEmail: string,
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(
    "/users/me/email-change-request",
    {
      method: "POST",
      headers: authHeaders(accessToken),
      body: { new_email: newEmail.trim() },
      signal,
    },
    undefined,
  );
}

export async function listMySessions(
  accessToken: string,
  signal?: AbortSignal,
): Promise<UserSession[]> {
  const res = await fetchJson<UserSession[] | null>(
    "/users/me/sessions",
    {
      headers: authHeaders(accessToken),
      signal,
    },
    undefined,
  );
  return Array.isArray(res) ? res : [];
}

export async function revokeMySession(
  accessToken: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(`/users/me/sessions/${encodeURIComponent(sessionId)}`, {
    method: "DELETE",
    headers: authHeaders(accessToken),
    signal,
  });
}

// ---- Tenant member management ----

export async function listTenantMembers(
  accessToken: string,
  tenantSlug: TenantSlug,
  signal?: AbortSignal,
): Promise<TenantMember[]> {
  const res = await fetchJson<unknown[] | null>(
    `/tenants/${encodeURIComponent(tenantSlug)}/members`,
    {
      headers: authHeaders(accessToken),
      signal,
    },
    undefined,
  );
  if (!Array.isArray(res)) return [];

  const mapped: TenantMember[] = [];
  for (const item of res) {
    const m = normalizeTenantMember(item);
    if (m) mapped.push(m);
  }
  return mapped;
}

export async function updateTenantMemberRole(
  accessToken: string,
  tenantSlug: TenantSlug,
  userId: string,
  role: TenantRole | string,
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(
    `/tenants/${encodeURIComponent(tenantSlug)}/members/${encodeURIComponent(userId)}`,
    {
      method: "PATCH",
      headers: authHeaders(accessToken),
      body: { role },
      signal,
    },
  );
}

export async function removeTenantMember(
  accessToken: string,
  tenantSlug: TenantSlug,
  userId: string,
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(
    `/tenants/${encodeURIComponent(tenantSlug)}/members/${encodeURIComponent(userId)}`,
    {
      method: "DELETE",
      headers: authHeaders(accessToken),
      signal,
    },
  );
}

export async function inviteTenantMember(
  accessToken: string,
  tenantSlug: TenantSlug,
  input: { email: string; role: TenantRole | string },
  signal?: AbortSignal,
): Promise<TenantInvitation> {
  return fetchJson<TenantInvitation>(
    `/tenants/${encodeURIComponent(tenantSlug)}/invitations`,
    {
      method: "POST",
      headers: authHeaders(accessToken),
      body: input,
      signal,
    },
    undefined,
  );
}

export async function listTenantInvitations(
  accessToken: string,
  tenantSlug: TenantSlug,
  signal?: AbortSignal,
): Promise<TenantInvitation[]> {
  const res = await fetchJson<TenantInvitation[] | null>(
    `/tenants/${encodeURIComponent(tenantSlug)}/invitations`,
    {
      headers: authHeaders(accessToken),
      signal,
    },
    undefined,
  );
  return Array.isArray(res) ? res : [];
}

export async function cancelTenantInvitation(
  accessToken: string,
  tenantSlug: TenantSlug,
  invitationId: string,
  signal?: AbortSignal,
): Promise<void> {
  await fetchJson<unknown>(
    `/tenants/${encodeURIComponent(tenantSlug)}/invitations/${encodeURIComponent(invitationId)}`,
    {
      method: "DELETE",
      headers: authHeaders(accessToken),
      signal,
    },
  );
}

